/**
 * The coach v2 wire, seen from the client.
 *
 * These are structural views of the GraphQL types in `Shared/coachEvents.graphql`, not re-exports of
 * the generated ones: the reducer is a framework-free library that any transport can drive, and
 * the generated types satisfy these shapes by construction (enums generate as string enums, which
 * are assignable to the `string` fields here). Anything the reducer does not read is left off, so
 * a caller can hand it the result of a richer selection set without mapping.
 */

/** Exactly one of `event` / `state` / `progress` is set on a frame, chosen by this. */
export const CoachFrameKind = {
  Event: 'EVENT',
  State: 'STATE',
  Progress: 'PROGRESS',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CoachFrameKind = (typeof CoachFrameKind)[keyof typeof CoachFrameKind];

/**
 * What the reducer needs off any event: `CoachSessionEvent` is a GraphQL interface so that
 * `id` and `seq` are readable without recognising which member arrived. Callers parameterise the
 * state on their own fuller event type; the reducer only ever touches these two fields.
 */
export interface CoachEventLike {
  __typename?: string | null;
  id: string;
  /** The position this event committed at. Null only on an event that has not been read back out of the log. */
  seq?: number | null;
  /**
   * The command this event answers for — the id the student's input went out under, and the same
   * id on the reply that turn committed. Null on everything that was nobody's command, and on
   * events written before the server recorded it.
   */
  clientRequestId?: string | null;
}

export interface CoachQuestionLike {
  /** The event the question rode in on, which is its identity and what an answer names it by. */
  id: string;
  /** `PLAIN` | `END_SESSION` | `GOAL_SWITCH` | `GOAL_SET` | `STARTERS`. */
  kind: string;
  /** `OPEN` | `ANSWERED` | `SUPERSEDED` | `EXPIRED`. Only `OPEN` is answerable. */
  state: string;
}

/**
 * One thing the server is currently willing to accept. `id` is what goes back on the wire: for the
 * generic kinds it is the kind's own value, for answering it is the question's event id.
 */
export interface CoachAvailableActionLike {
  id: string;
  kind: string;
  label: string;
}

export interface CoachSessionStateLike {
  id: string;
  /** `ACTIVE` | `ENDED`. */
  status: string;
  /** Bumps on every state mutation. The client rejects any STATE frame below the revision it holds. */
  revision: number;
  goalTitle?: string | null;
  goal?: string | null;
  /** Set the moment the coach judged the goal met; the session keeps running afterwards. */
  goalAchievedAt?: string | null;
  /**
   * The school whose lesson this goal came from, and null on a goal the coach holds. Provenance
   * only: what made it a school's goal — the source — is not on the wire, so nothing here can act on
   * it and the name is read where the goal is read.
   */
  goalSchoolName?: string | null;
  /**
   * The lesson this session is working through, and null when it is on none.
   *
   * Identity rather than permission: it is what lets a surface offering to open a lesson tell
   * whether it would be replacing anything. Tapping the lesson already running is a server-side
   * no-op and tapping a different one ends the chat, and those are opposite things to say to a
   * student. Nothing here may gate on it — every command that acts on a lesson re-asks the server.
   */
  lessonId?: string | null;
  /**
   * The session this one was opened behind, and null on a session that replaced nothing.
   *
   * It is what lets a client recognise a predecessor it never held. A tab that binds straight onto a
   * successor — every tab whose own first act opened it — meets that predecessor's ending on the
   * feed with nothing of its own that would say what it is, and an unrecognised session is a switch:
   * the client would rebind backwards onto the chat the handover had just closed. The successor
   * naming what it replaced is what closes that.
   */
  switchedFromSessionId?: string | null;
  openQuestion?: CoachQuestionLike | null;
  availableActions: CoachAvailableActionLike[];
  /**
   * The seq the last activity visit was closed at; null when none ever has been, so the whole
   * session is one visit still in progress. Every navigation above it belongs to the visit the
   * student is in now.
   *
   * It is on the wire because a close writes no event: the boundary is a number on the session row
   * and nothing else, so a tab that was not running when the visit started has nothing in the log
   * to find its beginning by.
   */
  activityClosedSeq?: number | null;
}

/**
 * The navigation that opened a screen, read back off the log. An info event is written against one
 * of these by id, so a surface holding an activity open has to be able to find the latest.
 */
export interface CoachActivityNavigationEventLike extends CoachEventLike {
  eventData: {
    activity: string;
  };
}

/**
 * Something the student did inside an activity, read back off the log. It names the navigation it
 * was done under, which is the only thing that says which screen it was done on — a save carries no
 * page of its own, and the visit it belongs to holds several.
 */
export interface CoachActivityInfoEventLike extends CoachEventLike {
  eventData: {
    infoType: string;
    activityNavigationEventId: string;
  };
}

/** A tool round starting or finishing. No seq, never persisted, never ordered. */
export interface CoachProgressLike {
  toolName: string;
  /** `STARTED` | `FINISHED`. */
  phase: string;
  /** The model round the call belongs to, from 1. */
  round: number;
  /** The call's place, from 1, among the calls its round's message carried. */
  callIndex: number;
  callsInRound: number;
}

export interface CoachFeedFrameLike<TEvent extends CoachEventLike = CoachEventLike> {
  sessionId: string;
  kind: CoachFrameKind;
  /** Set on EVENT and STATE. Null on PROGRESS, which the gap check skips. */
  seq?: number | null;
  event?: TEvent | null;
  state?: CoachSessionStateLike | null;
  progress?: CoachProgressLike | null;
}

export interface CoachSnapshotLike<TEvent extends CoachEventLike = CoachEventLike> {
  session: CoachSessionStateLike;
  /**
   * Ordered by seq and by nothing else. A no-cursor snapshot is compaction-filtered rather than
   * contiguous, so seqs are read off each event and never inferred from position.
   */
  events: TEvent[];
  /** The highest event seq this snapshot covers — the client's new `lastSeq`. */
  seq: number;
  /**
   * Whether a turn held the session at the moment of the read.
   *
   * The one thing here the log cannot say. A student's message with no reply under it is a turn
   * still thinking and a turn that fell over, and the transcript reads the same either way — so a
   * tab that reloaded mid-turn, or a second tab that never sent it, has nothing of its own that
   * would tell them apart. It arrives on the read rather than on the session state because a state
   * frame is published from inside the turn that wrote it: a client keeping the last one it saw
   * would be holding a `true` nothing later takes back.
   */
  turnInFlight: boolean;
  /**
   * Whether this snapshot carries an opening the server produced in this answer — a start that
   * birthed a session, handed over to a successor or ran the session's opening turn — as against a
   * start that resumed what was live. Only a start can say so; every read leaves it out.
   */
  carriesOpening?: boolean;
}

/** The ack for a command that runs the model. `acceptedSeq` is the only field a conflict clears. */
export interface CoachTurnAckLike {
  conflict: boolean;
  acceptedSeq?: number | null;
  revision?: number | null;
  latestSeq?: number | null;
}

/**
 * The ack for a command that writes but does not run the model. `applied: false` is a no-op, not an
 * error — unless `conflict` is set, which is the other way it comes back false: the write lost the
 * revision and nothing was written. The two are indistinguishable without it, and a caller that
 * leans on its write having landed has to be able to tell them apart.
 */
export interface CoachAckLike {
  applied: boolean;
  conflict: boolean;
  acceptedSeq?: number | null;
  revision?: number | null;
  latestSeq?: number | null;
}

export type CoachAnyAckLike = CoachTurnAckLike | CoachAckLike;

/**
 * What the surface shows about its own coherence. `gap-detected` is the moment a frame arrived out
 * of order; `refetching` is the cursor read that heals it.
 */
export const CoachSyncStatus = {
  Idle: 'idle',
  Live: 'live',
  GapDetected: 'gap-detected',
  Refetching: 'refetching',
  Error: 'error',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CoachSyncStatus = (typeof CoachSyncStatus)[keyof typeof CoachSyncStatus];

/**
 * Who makes the opening read when a sync starts — the sync itself, or the caller that has a better
 * answer than a read does. {@see CoachSyncStartOptions} for which caller that is and why.
 */
export const CoachSyncOpening = {
  Read: 'read',
  Deferred: 'deferred',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CoachSyncOpening = (typeof CoachSyncOpening)[keyof typeof CoachSyncOpening];

export interface CoachState<TEvent extends CoachEventLike = CoachEventLike> {
  /** The session the client is bound to. A frame naming another session is a switch, not a gap. */
  sessionId: string | null;
  /**
   * Every session this client has bound and left, most recent first, kept so a switch can only ever
   * go forwards: a durable frame or a read naming any of them is a late redelivery of a session
   * already left. One slot is not enough — after A→B→C a delayed A frame reads as an unseen session
   * and rebinds backwards onto an ended one. Bounded, and scoped to the sync's lifetime.
   */
  previousSessionIds: string[];
  session: CoachSessionStateLike | null;
  /** Per session. Reset on a session switch — a successor starts its own revision line at 0. */
  revision: number | null;
  /** The highest event seq applied for the bound session. Never moves backwards within a session. */
  lastSeq: number;
  events: TEvent[];
  /** Transient. Cleared by the next durable frame, by a snapshot, and by a command's ack or error. */
  progress: CoachProgressLike | null;
  /**
   * Bumps every time the client binds to a session it was not holding. A caller carrying work that
   * only means anything inside one binding — an info event named by a navigation id, a close it has
   * been waiting to send — reads it when the work starts and drops the work when it has moved,
   * rather than aiming it at a session the student never addressed.
   */
  bindingGeneration: number;
  /**
   * The binding generation a read has landed for, or null when none has yet.
   *
   * A binding is not a log. A rebind adopts an id and empties the transcript in the same breath,
   * and a STATE frame naming the new session can refill `session` while the read that refills the
   * events is still out — so `session !== null` says nothing about whether the log arrived. Only
   * this being equal to `bindingGeneration` says the events the client is holding are the ones this
   * binding's own read brought, which is the condition any reading of the log has to be taken under.
   */
  snapshotGeneration: number | null;
  /**
   * Whether the snapshot that last landed carries an opening ({@see CoachSnapshotLike.carriesOpening}),
   * stamped in the same commit as `snapshotGeneration`. False before any has landed and after a rebind. It is what tells a
   * surface drawing the landing whether the rows are an opening to be read out or a conversation
   * already under way.
   */
  snapshotCarriesOpening: boolean;
  /**
   * Whether a turn holds the session as far as this client can tell, and false until anything has
   * said so.
   *
   * A read is the authority, and the only thing that can take it back: no frame carries the flag,
   * because the frames a turn publishes go out from inside it and the way a turn ends badly
   * publishes nothing at all. What the log does say is where a turn's edges are — the student's own
   * line, written by the prelude of the turn about to run, and the coach's answer, which is that
   * turn's last word — so those two events move it between reads rather than leaving it as old as
   * the last one. It is read by the one thing that must not act on a turn still running: the offer
   * to run it again.
   */
  turnInFlight: boolean;
  status: CoachSyncStatus;
}

export const CoachResyncReason = {
  Gap: 'gap',
  SessionSwitch: 'session-switch',
  Reconnect: 'reconnect',
  Conflict: 'conflict',
  /** The server refused an answer because the question is no longer the one it is holding. */
  StaleQuestion: 'stale-question',
  /** The feed went quiet with nothing outstanding to explain it. The one repair, asked unprompted. */
  Idle: 'idle',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CoachResyncReason = (typeof CoachResyncReason)[keyof typeof CoachResyncReason];

/**
 * The one repair the protocol has. `cursor: null` asks for the compaction-windowed snapshot a tab
 * opens on; a cursor asks for the dense tail after that seq. `sessionId` is always named so a tab
 * holding a session that has since ended still reads its own tail rather than the active one.
 */
export interface CoachResyncRequest {
  sessionId: string | null;
  cursor: number | null;
  reason: CoachResyncReason;
}

export interface CoachReduceResult<TEvent extends CoachEventLike = CoachEventLike> {
  state: CoachState<TEvent>;
  resync: CoachResyncRequest | null;
}

/**
 * Which of the sync's own reads a failure came from.
 *
 * A caller's error channel is one line, and what may take a line back depends on what put it there:
 * a repair read that failed is the repair machinery's own claim on it, and a later repair that lands
 * has healed exactly what that line was about. The feed breaking, or the first read of all failing,
 * is not something a repair can answer for.
 */
export const CoachErrorSource = {
  Opening: 'opening',
  Feed: 'feed',
  Resync: 'resync',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CoachErrorSource = (typeof CoachErrorSource)[keyof typeof CoachErrorSource];
